import { RESULT_CODE } from "constants/enum";
import { useChangeLanguageMutation } from "@/services/settingAPI";
import sessionUtil from "@/utils/sessionUtil";
import {
  ActionIcon,
  Box,
  Group,
  Image,
  Menu,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { IconChevronDown } from "@tabler/icons-react";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

type Lang = "vi" | "en";

/**
 * #56448: tên ngôn ngữ viết BẰNG CHÍNH NGÔN NGỮ ĐÓ ở cả hai bản — "Tiếng Việt" và "English"
 * (QC chốt 17/09/2026). Trước đây dùng khoá i18n nên bản tiếng Anh ra "Vietnamese / English",
 * bản tiếng Việt ra "Tiếng Việt / Tiếng Anh" — người dùng đang ở bản mình không đọc được thì
 * không tìm ra mục cần chọn.
 *
 * Đây cũng là chỗ sửa một lỗi ngầm: mảng này nằm ở TẦNG MODULE nên `t()` chỉ chạy đúng một lần
 * lúc nạp tệp — nhãn đóng băng theo ngôn ngữ lúc tải trang, đổi ngôn ngữ xong nhãn vẫn cũ.
 * Chuỗi cố định thì không còn phụ thuộc thời điểm nữa.
 */
const LANGS: { value: Lang; label: string; flag: string }[] = [
  { value: "vi", label: "Tiếng Việt", flag: "https://flagcdn.com/w20/vn.png" },
  { value: "en", label: "English", flag: "https://flagcdn.com/w20/gb.png" },
];

const LanguageSelect: React.FC = () => {
  const theme = useMantineTheme();
  const { i18n } = useTranslation();

  const stored = (localStorage.getItem("language") || i18n.language) as Lang;
  const [language, setLanguage] = useState<Lang>(stored);

  const [changeLanguage, { isLoading }] = useChangeLanguageMutation();

  useEffect(() => {
    if (stored !== i18n.language) i18n.changeLanguage(stored);
  }, [i18n, stored]);

  const current = useMemo(
    () => LANGS.find((x) => x.value === language) ?? LANGS[0],
    [language]
  );

  /**
   * Đổi ngôn ngữ hiển thị.
   *
   * ⚠️ Việc đổi ngôn ngữ **không phụ thuộc backend**: `i18n.changeLanguage` + `localStorage` là đủ,
   * và `src/api/interceptors.tsx` đã tự lấy `body.header.language` từ `i18n.language` nên câu chữ
   * backend trả về cũng theo đúng ngôn ngữ đang bật. API `changeLanguage` chỉ để **ghi nhớ** lựa
   * chọn vào hồ sơ người dùng.
   *
   * Vì thế phải đổi giao diện TRƯỚC, rồi mới gọi API theo kiểu "được thì tốt".
   *
   * Component này chỉ xuất hiện ở các màn CHƯA đăng nhập (`PreLoginWrapper`, `LoginForm`,
   * `ChangePasswordPage`) — nơi không có `sessionId`. Mà `UserProfileForLoginService.changeLanguage`
   * (customer-service) đọc phiên từ Redis, không có phiên thì trả `ESM-9016`. Khoá việc đổi ngôn
   * ngữ theo kết quả lời gọi đó tức là nút đổi ngôn ngữ ở màn đăng nhập **không bao giờ chạy**:
   * máy nào còn phiên sống thì đổi được, máy nào phiên đã hết thì chịu — đúng hiện tượng người
   * dùng báo ngày 11/09/2026.
   */
  const handleLanguageChange = async (lang: Lang) => {
    setLanguage(lang);
    i18n.changeLanguage(lang);
    localStorage.setItem("language", lang);

    // Chưa đăng nhập thì không có gì để ghi nhớ — gọi API chỉ tổ nhận ESM-9016.
    if (!sessionUtil.hasSession()) {
      return;
    }
    try {
      const res = await changeLanguage((lang || "vi").toUpperCase()).unwrap();
      // axiosBaseQuery luôn trả {data} nên `.unwrap()` KHÔNG ném khi `resultCode != "0"`.
      const code = (res as any)?.body?.resultCode ?? (res as any)?.resultCode;
      if (code && code !== RESULT_CODE.SUCCESS) {
        // Không ghi nhớ được thì thôi — giao diện đã đổi rồi, không được quay ngược lại.
        const refId = (res as any)?.body?.header?.clientRequestId;
        console.warn("[changeLanguage] không lưu được lựa chọn ngôn ngữ", { code, refId });
      }
    } catch (e) {
      console.warn("[changeLanguage] không lưu được lựa chọn ngôn ngữ", e);
    }
  };

  return (
    <Menu withinPortal position="bottom-end" shadow="md" width={140}>
      <Menu.Target>
        <Box
          style={{
            display: "inline-flex",
            background: "#EBEBEB",
            padding: "13px 25px",
            borderRadius: "50px",
            cursor: "pointer",
          }}
        >
          <Group spacing={6}>
            <Image
              src={current.flag}
              alt={current.label}
              width={18}
              height={18}
              radius="xl"
            />
            <Text fw={700} style={{fontSize: "14px"}} c="#121826">
              {current.label}
            </Text>

            <ActionIcon
              variant="subtle"
              radius="md"
              size={22}
              disabled={isLoading}
              styles={{
                root: {
                  color: "#5E656B",
                },
              }}
            >
              <IconChevronDown size={16} />
            </ActionIcon>
          </Group>
        </Box>
      </Menu.Target>

      <Menu.Dropdown>
        {LANGS.map((item) => {
          const active = item.value === language;
          return (
            <Menu.Item
              key={item.value}
              onClick={() => handleLanguageChange(item.value)}
              disabled={isLoading || active}
              icon={
                <Image
                  src={item.flag}
                  alt={item.label}
                  width={18}
                  height={18}
                  radius="sm"
                />
              }
              styles={{
                item: {
                  fontWeight: 700,
                  fontSize: 12,
                  color: active ? theme.colors.blue[6] : "#111827",
                },
              }}
            >
              {item.label}
            </Menu.Item>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
};

export default LanguageSelect;
